/**
 * Get the API base URL based on environment.
 * If REACT_APP_PUBLIC_API_BASE_URL is set, use it (normalized).
 * Otherwise infer from REACT_APP_ENV/NODE_ENV.
 */
const stripWrappingQuotes = (value: string): string =>
  value.trim().replace(/^['"`\u2018\u2019\u201C\u201D]+|['"`\u2018\u2019\u201C\u201D]+$/g, '');

const normalizePublicApiBaseUrl = (rawValue: string): string => {
  const firstValue = stripWrappingQuotes(rawValue).split(',')[0]?.trim();
  if (!firstValue) return '';

  try {
    const url = new URL(firstValue);

    // Guard against app-domain values accidentally being used for API traffic.
    if (url.hostname === 'app.aptiveindex.com') url.hostname = 'api.aptiveindex.com';
    if (url.hostname === 'app2.aptiveindex.com') url.hostname = 'api2.aptiveindex.com';

    // Strip explicit HTTPS default backend port when present.
    if (url.protocol === 'https:' && url.port === '3001') {
      url.port = '';
    }

    const pathname = url.pathname === '/' ? '' : url.pathname.replace(/\/$/, '');
    return `${url.origin}${pathname}`;
  } catch {
    return firstValue;
  }
};

export const getApiBaseUrl = (): string => {
  if (process.env.REACT_APP_PUBLIC_API_BASE_URL) {
    const normalized = normalizePublicApiBaseUrl(process.env.REACT_APP_PUBLIC_API_BASE_URL);
    if (normalized) return normalized;
  }

  if (process.env.REACT_APP_ENV === 'staging') {
    return 'https://api2.aptiveindex.com';
  } else if (process.env.NODE_ENV === 'production') {
    return 'https://api.aptiveindex.com';
  } else {
    // Development
    return 'http://localhost:3001';
  }
};

// Export for backward compatibility
export const API_BASE_URL = getApiBaseUrl();

export const DEFUALT_TAGS = ['Employee', 'Candidate', 'Other', 'Archived', 'Terminated'];

// Prompts that can work without requiring a tagged person
// To add a new prompt that works without a person:
// 1. Add the prompt ID to this array
// 2. Add a case in generatePromptTemplateWithoutPerson() in chatPromptGenerator.ts
// 3. Ensure the prompt template doesn't rely on specific person data
export const PROMPTS_WITHOUT_PERSON = [
  'meetingTips', 
  'profileInsights',
  'selfExploration',
  'developmentTips',
  'trustDynamics',
  'threeSixtyFeedbackAnalysis',
  'targetRangeAnalysis',
  'positionScoping',
  'jobDescriptionRewrite'
] as const;

export const SHORT_URL_LENGTH = 12;